import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { Suggestion } from '../../aiSuggestionsPluginType';

export function buildSuggestionMaps(
	params:
		| {
				doc: PMNode;
				suggestions: Suggestion[];
		  }
		| undefined,
): {
	baseLocalIdBySuggestionId: Map<string, string>;
	orderedSuggestionIds: string[];
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
} {
	const suggestionById = new Map<string, Suggestion>();
	const suggestionIdsByLocalId = new Map<string, Set<string>>();
	const baseLocalIdBySuggestionId = new Map<string, string>();
	const suggestionIdsByBaseLocalId = new Map<string, Set<string>>();
	const orderedSuggestionIds: string[] = [];

	if (!params) {
		return {
			suggestionById,
			baseLocalIdBySuggestionId,
			orderedSuggestionIds,
			suggestionIdsByBaseLocalId,
		};
	}

	const { suggestions, doc } = params;

	for (const suggestion of suggestions) {
		suggestionById.set(suggestion.id, suggestion);
		const firstToolCall = suggestion.toolCalls[0];
		/**
		 * Only add the first tool call to the suggestionIdsByLocalId map
		 * to simplify navigation, stacking, and getting logic.
		 */
		if (firstToolCall) {
			const localId =
				'localId' in firstToolCall.arguments ? firstToolCall.arguments.localId : undefined;
			if (localId) {
				const ids = suggestionIdsByLocalId.get(localId) ?? new Set<string>();
				ids.add(suggestion.id);
				suggestionIdsByLocalId.set(localId, ids);
			}
		}
	}

	// Map each top-level block position (doc depth 1) to suggestion IDs that target any localId
	// under that block — same docLevelPos as `doc.resolve(pos).before(1)` in the decorations plugin.

	doc.descendants((node, pos) => {
		const nodeLocalId = node.attrs?.localId;
		if (!nodeLocalId) {
			return true;
		}
		const suggestionIdsAtLocalId = suggestionIdsByLocalId.get(nodeLocalId);
		if (suggestionIdsAtLocalId?.size) {
			const $pos = doc.resolve(pos);
			const baseLocalId = doc.nodeAt($pos.before(1))?.attrs?.localId;
			if (!baseLocalId) {
				return true;
			}
			suggestionIdsAtLocalId.forEach((suggestionId) => {
				// A localId can repeat across the doc (e.g. clones produced by collab/copy-paste).
				// Keep the first-encountered mapping so each suggestion is anchored once and appears
				// exactly once in the navigation order, otherwise the card stalls on duplicates.
				if (baseLocalIdBySuggestionId.has(suggestionId)) {
					return;
				}
				// each suggestion maps to a base local id
				baseLocalIdBySuggestionId.set(suggestionId, baseLocalId);
				// we use doc.descendants order to determine the order of the suggestions
				orderedSuggestionIds.push(suggestionId);
			});
			const baseSet = suggestionIdsByBaseLocalId.get(baseLocalId) ?? new Set<string>();
			const childSet = suggestionIdsByLocalId.get(nodeLocalId);
			if (childSet) {
				// concat it into the base set
				suggestionIdsByBaseLocalId.set(baseLocalId, new Set([...baseSet, ...childSet]));
			}
		}
		return true;
	});

	return {
		suggestionById,
		baseLocalIdBySuggestionId,
		orderedSuggestionIds,
		suggestionIdsByBaseLocalId,
	};
}
